import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { jur } from "@/lib/api";
import { toast } from "sonner";
import { Scale, FileSignature, Gavel, ClipboardCheck, Sparkles, ArrowLeft, LogOut, AlertTriangle } from "lucide-react";

export default function Juridico() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [pareceres, setPareceres] = useState([]);
  const [adequacao, setAdequacao] = useState([]);
  const [showParecer, setShowParecer] = useState(false);

  useEffect(() => { load(); }, [tab]);
  const load = async () => {
    try {
      if (tab === "dashboard") setDash((await jur.dashboard()).data);
      if (tab === "contratos") setContratos((await jur.contratos()).data);
      if (tab === "processos") setProcessos((await jur.processos()).data);
      if (tab === "pareceres") setPareceres((await jur.pareceres()).data);
      if (tab === "adequacao") setAdequacao((await jur.adequacao()).data);
    } catch { toast.error("Erro"); }
  };
  const fmt = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className="min-h-screen bg-[#0A0B1A] flex flex-col">
      <header className="bg-gradient-to-r from-[#1A1D42] to-[#252952] border-b border-[#B28F4B]/25 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center"><Scale size={20} className="text-[#B28F4B]"/></div>
          <div className="flex-1">
            <div className="text-[#B28F4B] font-bold font-heading text-lg">SOEASY · Administrativo & Jurídico</div>
            <div className="text-white/60 text-[10px] uppercase tracking-widest">Gestão integrada · Contratos · Processos · Compliance Normativo</div>
          </div>
          <div className="hidden md:flex gap-2">
            <span className="px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px] font-bold uppercase tracking-widest">CLT · CDC</span>
            <span className="px-2.5 py-1 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 text-[9px] font-bold uppercase tracking-widest">LGPD · ANPD</span>
            <span className="px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/40 text-purple-300 text-[9px] font-bold uppercase tracking-widest">Cód. Civil · Súmulas</span>
          </div>
          <button onClick={() => setShowParecer(true)} data-testid="btn-parecer-ia" className="px-3 py-1.5 rounded-md bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] text-xs font-bold uppercase tracking-widest flex items-center gap-1"><Sparkles size={12}/> Parecer IA</button>
          <button onClick={() => navigate("/setores")} data-testid="btn-back-setores" className="px-3 py-1.5 rounded-md border border-[#B28F4B]/40 text-white text-xs font-bold uppercase tracking-widest">Setores</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold uppercase tracking-widest">Sair</button>
        </div>
        <div className="max-w-[1600px] mx-auto px-5 flex gap-1 overflow-x-auto">
          {[
            { id: "dashboard", label: "Central Adm-Jurídica", icon: ClipboardCheck },
            { id: "contratos", label: "Contratos", icon: FileSignature },
            { id: "processos", label: "Processos Judiciais", icon: Gavel },
            { id: "pareceres", label: "Pareceres IA", icon: Sparkles },
            { id: "adequacao", label: "Adequação Normativa", icon: AlertTriangle },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-jur-${t.id}`}
              className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 flex items-center gap-1.5 ${tab===t.id?"border-[#B28F4B] text-[#B28F4B]":"border-transparent text-white/60 hover:text-white"}`}>
              <t.icon size={13}/> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto p-5 fade-up">
        {tab === "dashboard" && dash && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <JurKpi label="Contratos Ativos" value={dash.contratos_ativos} sub={`${dash.contratos_vencendo_60d} vencem em 60d`} color="#B28F4B"/>
              <JurKpi label="Em Análise Jurídica" value={dash.contratos_em_analise} sub="aguardando parecer" color="#F59E0B"/>
              <JurKpi label="Processos Ativos" value={dash.processos_ativos} sub={`${dash.processos_criticos} críticos`} color="#EF4444"/>
              <JurKpi label="Provisão Contábil" value={fmt(dash.provisao_total)} sub="processos judiciais" color="#22D3EE"/>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
                <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Processos por Tipo</div>
                {Object.entries(dash.por_tipo_processo || {}).map(([k, v]) => {
                  const max = Math.max(...Object.values(dash.por_tipo_processo), 1);
                  return (
                    <div key={k} className="mb-2">
                      <div className="flex justify-between text-xs mb-1"><span className="text-white uppercase">{k}</span><span className="text-[#B28F4B] font-mono font-bold">{v}</span></div>
                      <div className="h-1.5 bg-[#0A0B1A] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#B28F4B] to-[#D4AF37]" style={{width: `${v/max*100}%`}}/></div>
                    </div>
                  );
                })}
              </div>
              <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
                <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Adequação Normativa</div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <div className="text-emerald-400 text-2xl font-mono font-bold">{(dash.adequacao_total - dash.adequacao_nao_conforme - dash.adequacao_em_adequacao) || 0}</div>
                    <div className="text-emerald-300 text-[9px] font-bold uppercase">Conforme</div>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="text-amber-400 text-2xl font-mono font-bold">{dash.adequacao_em_adequacao}</div>
                    <div className="text-amber-300 text-[9px] font-bold uppercase">Em Adequação</div>
                  </div>
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                    <div className="text-red-400 text-2xl font-mono font-bold">{dash.adequacao_nao_conforme}</div>
                    <div className="text-red-300 text-[9px] font-bold uppercase">Não Conforme</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "contratos" && (
          <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25 text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]"><tr><th className="p-3">Nº Contrato</th><th className="p-3">Título</th><th className="p-3">Contraparte</th><th className="p-3">Tipo</th><th className="p-3 text-right">Valor</th><th className="p-3">Vigência</th><th className="p-3">Status</th></tr></thead>
              <tbody>
                {contratos.map(c => (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3 text-[#B28F4B] font-mono text-xs">{c.numero}</td>
                    <td className="p-3 text-white text-xs font-bold">{c.titulo}</td>
                    <td className="p-3 text-[#A1A4C7] text-xs">{c.contraparte}</td>
                    <td className="p-3 text-[#A1A4C7] text-[10px] uppercase">{c.tipo}</td>
                    <td className="p-3 text-right text-white font-mono">{fmt(c.valor)}</td>
                    <td className="p-3 text-[#A1A4C7] text-[10px] font-mono">{c.data_inicio} → {c.data_fim}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${c.status==="ativo"?"bg-emerald-500/15 text-emerald-400":c.status==="em_analise_juridica"?"bg-amber-500/15 text-amber-400":"bg-slate-500/15 text-slate-400"}`}>{c.status.replace(/_/g," ")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "processos" && (
          <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25 text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]"><tr><th className="p-3">Nº CNJ</th><th className="p-3">Tipo</th><th className="p-3">Parte Contrária</th><th className="p-3 text-right">Valor Causa</th><th className="p-3 text-right">Provisão</th><th className="p-3">Fase</th><th className="p-3">Risco</th></tr></thead>
              <tbody>
                {processos.map(p => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3 text-[#B28F4B] font-mono text-[10px]">{p.numero_cnj}</td>
                    <td className="p-3 text-[#A1A4C7] text-[10px] uppercase">{p.tipo}</td>
                    <td className="p-3 text-white text-xs font-bold">{p.parte_contraria}</td>
                    <td className="p-3 text-right text-white font-mono">{fmt(p.valor_causa)}</td>
                    <td className="p-3 text-right text-cyan-400 font-mono">{fmt(p.provisao_contabil)}</td>
                    <td className="p-3 text-[#A1A4C7] text-xs uppercase">{p.fase}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${p.risco==="critico"?"bg-red-500/25 text-red-300":p.risco==="alto"?"bg-orange-500/20 text-orange-400":p.risco==="medio"?"bg-amber-500/15 text-amber-400":"bg-emerald-500/15 text-emerald-400"}`}>{p.risco}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "pareceres" && (
          <div className="space-y-3">
            {pareceres.length === 0 && <div className="p-16 text-center text-[#A1A4C7]">Nenhum parecer emitido. Use o botão "Parecer IA" no topo para gerar.</div>}
            {pareceres.map(p => (
              <div key={p.id} className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded bg-[#B28F4B]/15 text-[#B28F4B] text-[9px] font-bold uppercase tracking-widest">{p.tipo?.replace(/_/g," ")}</span>
                  <span className="px-2 py-0.5 rounded bg-purple-500/15 text-purple-400 text-[9px] font-bold uppercase tracking-widest">Claude 4.6</span>
                  <span className="text-[#6B6E8F] text-[10px] font-mono">{p.created_at?.slice(0, 10)}</span>
                </div>
                <div className="text-white font-bold text-sm mb-2">{p.titulo}</div>
                <div className="text-[#A1A4C7] text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">{p.parecer}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "adequacao" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {adequacao.map(a => (
              <div key={a.id} className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2 h-2 rounded-full ${a.status==="conforme"?"bg-emerald-500":a.status==="nao_conforme"?"bg-red-500":"bg-amber-500"}`}/>
                  <span className={`text-[9px] font-bold uppercase tracking-widest ${a.status==="conforme"?"text-emerald-400":a.status==="nao_conforme"?"text-red-400":"text-amber-400"}`}>{a.status?.replace(/_/g," ")}</span>
                </div>
                <div className="text-[#B28F4B] font-bold text-sm mb-1">{a.norma}</div>
                <div className="text-white/70 text-xs mb-2">{a.descricao}</div>
                <div className="text-[10px] text-[#6B6E8F] uppercase tracking-widest">Responsável: <span className="text-white/80 normal-case">{a.responsavel}</span></div>
                {a.prazo && <div className="text-[10px] text-[#6B6E8F] font-mono">Prazo: {a.prazo}</div>}
              </div>
            ))}
          </div>
        )}
      </main>

      {showParecer && <ModalParecerIA onClose={() => { setShowParecer(false); load(); }}/>}
    </div>
  );
}

function JurKpi({ label, value, sub, color }) {
  return (
    <div className="bg-gradient-to-br from-[#131532] to-transparent border border-[#B28F4B]/25 rounded-2xl p-4">
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#A1A4C7]">{label}</div>
      <div className="font-mono font-bold text-2xl mt-1" style={{color}}>{value}</div>
      <div className="text-[9px] text-[#6B6E8F] mt-0.5 uppercase tracking-widest">{sub}</div>
    </div>
  );
}

function ModalParecerIA({ onClose }) {
  const [titulo, setTitulo] = useState("");
  const [tipo, setTipo] = useState("consulta");
  const [contexto, setContexto] = useState("");
  const [loading, setLoading] = useState(false);
  const [parecer, setParecer] = useState(null);
  const gerar = async () => {
    if (!titulo || !contexto) return toast.error("Preencha título e contexto");
    setLoading(true);
    try {
      const r = await jur.criarParecer({ titulo, tipo, contexto, urgencia: "media" });
      setParecer(r.data.parecer);
      toast.success("Parecer emitido");
    } catch { toast.error("Erro na IA"); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="bg-[#131532] border border-[#B28F4B]/40 rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-[#B28F4B] font-heading text-lg font-bold mb-4 flex items-center gap-2"><Sparkles size={18}/> Emitir Parecer Jurídico com IA</h3>
        <div className="space-y-3">
          <input placeholder="Título do parecer" value={titulo} onChange={e=>setTitulo(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm"/>
          <select value={tipo} onChange={e=>setTipo(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm">
            <option value="consulta">Consulta</option><option value="opiniao_legal">Opinião Legal</option><option value="risco_operacional">Risco Operacional</option><option value="adequacao_normativa">Adequação Normativa</option><option value="due_diligence">Due Diligence</option>
          </select>
          <textarea rows={5} placeholder="Descreva o contexto, fatos e a consulta jurídica..." value={contexto} onChange={e=>setContexto(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm"/>
          <button onClick={gerar} disabled={loading} data-testid="btn-gerar-parecer" className="w-full py-2.5 rounded-lg bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
            <Sparkles size={13}/> {loading ? "Elaborando parecer..." : "Emitir Parecer com Claude 4.6"}
          </button>
          {parecer && (
            <div className="mt-4 p-4 bg-[#0A0B1A] rounded-lg border border-[#B28F4B]/25 text-white/80 text-xs whitespace-pre-wrap max-h-96 overflow-y-auto">{parecer}</div>
          )}
        </div>
      </div>
    </div>
  );
}
